import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebase';

const AuthContext = createContext(null);

const ADMIN_EMAIL = 'david.zickafoose@gmail.com';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set persistence on mount
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('[Auth] Persistence set to LOCAL');
      })
      .catch((err) => {
        console.error('[Auth] Failed to set persistence:', err);
      });
  }, []);

  useEffect(() => {
    console.log('[Auth] Setting up onAuthStateChanged listener');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[Auth] onAuthStateChanged fired:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user');

      if (firebaseUser) {
        try {
          const userDataFromDb = await fetchOrCreateUser(firebaseUser);
          console.log('[Auth] User data fetched successfully:', userDataFromDb.email);
          setUser(firebaseUser);
          setUserData(userDataFromDb);
          setError(null);
        } catch (err) {
          console.error('[Auth] Error fetching user data:', err);
          setUser(firebaseUser);
          setUserData(null);
          setError(err.message);
        }
      } else {
        console.log('[Auth] No user - clearing state');
        setUser(null);
        setUserData(null);
        setError(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const fetchOrCreateUser = async (firebaseUser) => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return { uid: firebaseUser.uid, ...userSnap.data() };
    }

    // Auto-create admin user
    if (firebaseUser.email === ADMIN_EMAIL) {
      const adminData = {
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        role: 'admin',
        modules: ['crl', 'insider', 'company'],
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      await setDoc(userRef, adminData);
      return { uid: firebaseUser.uid, ...adminData };
    }

    // Check if user was pre-created with email-based document ID (pending user)
    const emailDocId = firebaseUser.email.replace('@', '_at_').replace(/\./g, '_dot_');
    const pendingUserRef = doc(db, 'users', emailDocId);
    const pendingUserSnap = await getDoc(pendingUserRef);

    if (pendingUserSnap.exists()) {
      // Migrate pending user to use Firebase UID as document ID
      const pendingData = pendingUserSnap.data();
      const migratedData = {
        ...pendingData,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        pending: false,
        updated_at: serverTimestamp(),
      };

      // Create new document with Firebase UID
      await setDoc(userRef, migratedData);

      // Delete the old email-based document
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(pendingUserRef);

      console.log('[Auth] Migrated pending user to UID-based document');
      return { uid: firebaseUser.uid, ...migratedData };
    }

    // User not in database and not admin - access denied
    throw new Error('Access denied. Please contact administrator.');
  };

  const signIn = async () => {
    try {
      setError(null);
      console.log('[Auth] Starting sign in with popup...');
      console.log('[Auth] Current domain:', window.location.hostname);

      const result = await signInWithPopup(auth, googleProvider);
      console.log('[Auth] Sign in successful:', result.user?.email);
      console.log('[Auth] User UID:', result.user?.uid);
      return result;
    } catch (err) {
      // Handle popup closed by user
      if (err.code === 'auth/popup-closed-by-user') {
        console.log('[Auth] User closed the popup');
        return null;
      }
      // Handle popup blocked
      if (err.code === 'auth/popup-blocked') {
        console.error('[Auth] Popup was blocked');
        setError('Popup was blocked. Please allow popups for this site.');
        throw err;
      }
      // Handle unauthorized domain
      if (err.code === 'auth/unauthorized-domain') {
        console.error('[Auth] UNAUTHORIZED DOMAIN - Add this domain to Firebase Console:', window.location.hostname);
        setError(`This domain (${window.location.hostname}) is not authorized. Please add it to Firebase Console > Authentication > Settings > Authorized domains.`);
        throw err;
      }
      console.error('[Auth] Sign in error:', err.code, err.message);
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserData(null);
    } catch (err) {
      console.error('Sign out error:', err);
      throw err;
    }
  };

  const hasModuleAccess = (moduleName) => {
    if (!userData) return false;
    if (userData.role === 'admin') return true;
    // Company Intelligence is available to all authenticated users
    if (moduleName === 'company') return true;
    return userData.modules?.includes(moduleName);
  };

  const isAdmin = () => {
    return userData?.role === 'admin';
  };

  const value = {
    user,
    userData,
    loading,
    error,
    signIn,
    signOut,
    hasModuleAccess,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
